import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category/guanxuan", label: "官宣恋情" },
  { to: "/category/fanche", label: "翻车现场" },
  { to: "/category/huifu", label: "回应声明" },
  { to: "/category/juzu", label: "剧组风云" },
  { to: "/about", label: "关于我们" },
];

const glassBtn =
  "bg-background-50/20 text-background-50 hover:bg-background-50/30 dark:bg-foreground-950/15 dark:text-foreground-950 dark:hover:bg-foreground-950/25";

function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [isDark, setIsDark] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    const stored = window.localStorage.getItem("guaspeed-theme");
    if (stored === "dark") {
      document.documentElement.classList.add("dark");
      setIsDark(true);
    }
  }, []);

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("dark", next);
    window.localStorage.setItem("guaspeed-theme", next ? "dark" : "light");
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      return;
    }
    navigate(`/category/all?q=${encodeURIComponent(value)}`);
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-gradient-to-b from-foreground-950/70 to-transparent dark:from-background-50/70"
      }`}
    >
      <div className="w-full px-4 md:px-10 py-3 md:py-4 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0 cursor-pointer" aria-label="瓜圈速报首页">
          <span className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 font-heading text-xl font-black leading-none">
            瓜
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={`font-heading text-lg md:text-xl font-black tracking-wide ${
                solid ? "text-foreground-950" : "text-background-50 dark:text-foreground-950"
              }`}
            >
              瓜圈速报
            </span>
            <span
              className={`hidden md:block text-[10px] tracking-[0.2em] mt-1 ${
                solid ? "text-foreground-500" : "text-background-200 dark:text-foreground-800"
              }`}
            >
              第一手快讯 · 最新进展抢先看
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  isActive
                    ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                    : solid
                      ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                      : "text-background-100 hover:text-background-50 hover:bg-foreground-950/30 dark:text-foreground-800 dark:hover:text-foreground-950 dark:hover:bg-foreground-950/20"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <span
            className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
              solid
                ? "bg-accent-100 text-accent-900"
                : "bg-background-50/20 text-background-50 dark:bg-foreground-950/15 dark:text-foreground-950"
            }`}
          >
            <span className="w-2 h-2 rounded-full bg-primary-500 animate-live" />
            速报进行中
          </span>

          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            aria-label="搜索速报"
            className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
              solid ? "bg-background-100 text-foreground-700 hover:bg-background-200" : glassBtn
            }`}
          >
            <i className="ri-search-line text-lg" />
          </button>

          <button
            type="button"
            onClick={toggleTheme}
            aria-label="切换深浅色"
            className={`w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
              solid ? "bg-background-100 text-foreground-700 hover:bg-background-200" : glassBtn
            }`}
          >
            <i className={`${isDark ? "ri-sun-line" : "ri-moon-line"} text-lg`} />
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="切换菜单"
            aria-expanded={menuOpen}
            className={`lg:hidden w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
              solid ? "bg-background-100 text-foreground-700 hover:bg-background-200" : glassBtn
            }`}
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="w-full px-4 md:px-10 pb-4">
          <form
            onSubmit={submitSearch}
            className="max-w-2xl mx-auto flex items-center gap-2 bg-background-50 border border-background-300 rounded-lg px-3 py-2"
          >
            <span className="w-6 h-6 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              ref={inputRef}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="text"
              name="keyword"
              placeholder="搜速报关键词，例如「官宣」「回应」"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none py-1"
            />
            <button
              type="submit"
              className="px-4 py-1.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3">
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                    isActive
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

export default SiteHeader;